import { createLocalJWKSet, createRemoteJWKSet, errors as joseErrors, type JSONWebKeySet, type JWTVerifyGetKey } from "jose";
import { apiUrl } from "./apiUrl";
import { API_BASE_URL } from "@/api/base";
import { IDENTITY_JWKS_PATH, isIdentityToken } from "./identity";

/**
 * Pinksy's published session-signing keys (`GET /.well-known/jwks.json`, RFC 7517), as a jose key
 * resolver: fetched on first use, cached, and refetched — rate-limited — when a token names a `kid`
 * the cache has not seen, which is what a key rotation looks like from here.
 *
 * The last key set fetched is also kept in localStorage (P-09): a launch with no signal still has to
 * verify the token it holds before it draws anything, and the JWKS is the one network read on that
 * path. When the fetch fails (offline, the app down) the cached document verifies instead; a JOSE
 * rejection — bad signature, unknown kid — is never masked.
 */
export const JWKS_PATH = "/.well-known/jwks.json";
/** Minimum gap between two refetches provoked by unknown kids. */
export const JWKS_COOLDOWN_MS = 30_000;
/** How long a fetched key set is trusted before a background refresh. */
export const JWKS_CACHE_MAX_AGE_MS = 10 * 60_000;
/** localStorage: `{ url, jwks }` — the last key set fetched from that URL. */
export const JWKS_STORAGE_KEY = "pinksy:jwks";

interface StoredJwks {
  url: string;
  jwks: JSONWebKeySet;
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

const isKeySet = (value: unknown): value is JSONWebKeySet =>
  typeof value === "object" && value !== null && Array.isArray((value as JSONWebKeySet).keys);

/** The key set last fetched from `url`, or null when there is none (or it is for another host). */
export function readCachedJwks(url: string, store: Storage | null = storage()): JSONWebKeySet | null {
  try {
    const raw = store?.getItem(JWKS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredJwks>;
    return parsed.url === url && isKeySet(parsed.jwks) ? parsed.jwks : null;
  } catch {
    return null;
  }
}

export function writeCachedJwks(url: string, jwks: JSONWebKeySet, store: Storage | null = storage()): void {
  try {
    store?.setItem(JWKS_STORAGE_KEY, JSON.stringify({ url, jwks } satisfies StoredJwks));
  } catch {
    // best-effort — the next offline launch then has no keys to verify with
  }
}

/** A fetch that never happened — offline, refused, timed out — as opposed to keys that did not match. */
export function isFetchFailure(err: unknown): boolean {
  if (err instanceof joseErrors.JWKSTimeout) return true;
  return !(err instanceof joseErrors.JOSEError);
}

export function createJwks(base: string = API_BASE_URL, store: Storage | null = storage(), path: string = JWKS_PATH): JWTVerifyGetKey {
  const url = apiUrl(path, base);
  const remote = createRemoteJWKSet(url, { cooldownDuration: JWKS_COOLDOWN_MS, cacheMaxAge: JWKS_CACHE_MAX_AGE_MS });
  const cached = readCachedJwks(url.href, store);
  let local: JWTVerifyGetKey | null = cached === null ? null : createLocalJWKSet(cached);

  return async (protectedHeader, token) => {
    try {
      const key = await remote(protectedHeader, token);
      const document = remote.jwks();
      if (document) {
        writeCachedJwks(url.href, document, store);
        local = createLocalJWKSet(document);
      }
      return key;
    } catch (err) {
      if (local !== null && isFetchFailure(err)) return local(protectedHeader, token);
      throw err;
    }
  };
}

let deskJwks: JWTVerifyGetKey | null = null;
let identityJwks: JWTVerifyGetKey | null = null;
let combined: JWTVerifyGetKey | null = null;

/**
 * 🔴 **TWO ISSUERS, AND THE TOKEN SAYS WHICH** (D84; three until PNK-106).
 *
 * Better Auth signs a password, passkey or social sign-in; Pinksy's own D53 mint is still here while
 * it is retired. They are signed with different keys, so a token has to be verified against the right
 * key set: sent to the wrong one, a perfectly good session reads as a forged signature and the screen
 * says "sign-in did not complete" with nothing wrong anywhere.
 *
 * The third issuer was the Passport, which signed passkeys and assisted sessions (D34) from a whole
 * other app. It is gone, and both remaining key sets are served by Pinksy itself.
 *
 * The token chooses, and it is not a trust decision — it only picks which public keys to try, and the
 * signature still has to verify against them — so reading the header before verification is safe.
 */
export function sessionJwks(): JWTVerifyGetKey {
  // Memoised, and the identity matters: each underlying resolver holds a fetch cooldown and a cached
  // key set, so handing back a fresh closure per call would quietly re-fetch both key sets forever.
  if (combined !== null) return combined;
  const desk = (deskJwks ??= createJwks(API_BASE_URL));
  const identity = (identityJwks ??= createJwks(API_BASE_URL, storage(), IDENTITY_JWKS_PATH));
  combined = async (protectedHeader, token) => (isIdentityToken(token) ? identity : desk)(protectedHeader, token);
  return combined;
}

/** Tests: forget the bound resolvers (and their caches). */
export function resetSessionJwks(): void {
  deskJwks = null;
  identityJwks = null;
  combined = null;
}
